export class Vec3 {
  public static Null(): Readonly<Vec3> {
    return Null
  }

  public static X(): Readonly<Vec3> {
    return X
  }

  public static Y(): Readonly<Vec3> {
    return Y
  }

  public static Z(): Readonly<Vec3> {
    return Z
  }

  public static Left(): Readonly<Vec3> {
    return Left
  }

  public static Right(): Readonly<Vec3> {
    return Right
  }

  public static Up(): Readonly<Vec3> {
    return Up
  }

  public static Down(): Readonly<Vec3> {
    return Down
  }

  public static Forward(): Readonly<Vec3> {
    return Forward
  }

  public static Back(): Readonly<Vec3> {
    return Back
  }

  public readonly x: number
  public readonly y: number
  public readonly z: number

  constructor(x: number = 0, y: number = 0, z: number = 0) {
    this.x = x
    this.y = y
    this.z = z
  }

  public equals(other: Vec3): boolean {
    return this === other || (this.x === other.x && this.y === other.y && this.z === other.z)
  }

  public add(other: Vec3): Readonly<Vec3> {
    return new Vec3(this.x + other.x, this.y + other.y, this.z + other.z)
  }

  public sub(other: Vec3): Readonly<Vec3> {
    return new Vec3(this.x - other.x, this.y - other.y, this.z - other.z)
  }

  public addScaled(other: Vec3, scale: number): Readonly<Vec3> {
    return new Vec3(this.x + other.x * scale, this.y + other.y * scale, this.z + other.z * scale)
  }

  public scale(f: number): Readonly<Vec3> {
    return new Vec3(this.x * f, this.y * f, this.z * f)
  }

  public reverse(): Readonly<Vec3> {
    return new Vec3(-this.x, -this.y, -this.z)
  }

  public dot(other: Vec3): number {
    return this.x * other.x + this.y * other.y + this.z * other.z
  }

  public cross(other: Vec3): Readonly<Vec3> {
    return new Vec3(
      this.y * other.z - this.z * other.y,
      this.z * other.x - this.x * other.z,
      this.x * other.y - this.y * other.x,
    )
  }

  public lenSq(): number {
    return this.x ** 2 + this.y ** 2 + this.z ** 2
  }

  public len(): number {
    return Math.sqrt(this.x ** 2 + this.y ** 2 + this.z ** 2)
  }

  public normalize(): Readonly<Vec3> {
    const l = 1.0 / this.len()
    return new Vec3(this.x * l, this.y * l, this.z * l)
  }

  public distSq(other: Vec3): number {
    return (other.x - this.x) ** 2 + (other.y - this.y) ** 2 + (other.z - this.z) ** 2
  }

  public dist(other: Vec3): number {
    return Math.sqrt((other.x - this.x) ** 2 + (other.y - this.y) ** 2 + (other.z - this.z) ** 2)
  }

  public angleX(): number {
    return this.angleTo(X)
  }

  public angleY(): number {
    return this.angleTo(Y)
  }

  public angleZ(): number {
    return this.angleTo(Z)
  }

  public angleTo(other: Vec3): number {
    return Math.acos(this.dot(other) / (this.len() * other.len()))
  }

  public lerp(other: Vec3, t: number): Readonly<Vec3> {
    if (t <= 0) {
      return this
    } else if (t >= 1.0) {
      return other
    } else {
      return new Vec3(this.x + (other.x - this.x) * t, this.y + (other.y - this.y) * t, this.z + (other.z - this.z) * t)
    }
  }

  public nlerp(other: Vec3, t: number): Readonly<Vec3> {
    return this.lerp(other, t).normalize()
  }

  public slerp(other: Vec3, t: number): Readonly<Vec3> {
    if (t <= 0) {
      return new Vec3(this.x, this.y, this.z)
    } else if (t >= 1.0) {
      return new Vec3(other.x, other.y, other.z)
    } else {
      const omega = this.angleTo(other)
      const sinOmega = Math.sin(omega)
      const tOmega = t * omega
      const fSrc = Math.sin(omega - tOmega) / sinOmega
      const fDst = Math.sin(tOmega) / sinOmega
      const pSrc = this.scale(fSrc)
      const pDst = other.scale(fDst)
      return pSrc.add(pDst)
    }
  }

  public project(other: Vec3): Readonly<Vec3> {
    return other.scale(this.dot(other) / other.lenSq())
  }
}

const Null = Object.freeze(new Vec3())
const X = Object.freeze(new Vec3(1, 0, 0))
const Y = Object.freeze(new Vec3(0, 1, 0))
const Z = Object.freeze(new Vec3(0, 0, 1))
const Right = X
const Left = Object.freeze(new Vec3(-1, 0, 0))
const Up = Y
const Down = Object.freeze(new Vec3(0, -1, 0))
const Back = Z
const Forward = Object.freeze(new Vec3(0, 0, -1))
